/**
 * AI分析機能のモックサービス
 * 実際のAPI実装前のプロトタイプ用
 */

import logger from '../utils/logger';

export interface AIProfileSummary {
  customer_type: string;
  behavioral_patterns: string[];
  recommended_services: string[];
  service_notes: string[];
  generated_at: string;
  expires_at: string;
  is_cached: boolean;
}

// 顧客タイプのパターン
const customerTypes = [
  'VIP',
  'リピーター',
  '新規',
  '休眠顧客'
];

// 行動パターンのバリエーション
const behavioralPatternsPool = [
  '月1-2回の定期来店',
  '週末午後の予約が多い',
  '平日夜間の利用が中心',
  '不定期だが長時間コース利用',
  '友人・家族と一緒に来店',
  'キャンペーン時に集中来店',
  '季節の変わり目に来店増加',
  '予約変更・キャンセルが少ない'
];

// おすすめサービスのバリエーション
const recommendedServicesPool = [
  'プレミアムヘッドスパコース',
  'アロマオイルトリートメント',
  '眼精疲労ケアオプション',
  'ホットストーン追加',
  'フェイシャルケアセット',
  '首肩集中ケア',
  'リンパドレナージュ',
  '全身もみほぐし90分',
  'デトックスコース',
  '美容鍼灸オプション'
];

// サービスノートのバリエーション
const serviceNotesPool = [
  '静かな環境を好む',
  '強めの圧は避ける',
  'アロマの香りに敏感',
  '首周りは特に優しく',
  '会話を楽しみたいタイプ',
  '施術中は休みたい',
  '冷え性なので温度調整注意',
  '腰痛持ちのため体勢に配慮'
];

/**
 * プロファイル要約を生成（モック）
 * @param customerId 顧客ID
 * @returns AI分析結果
 */
export const mockGenerateProfileSummary = async (customerId: number): Promise<AIProfileSummary> => {
  // 3秒遅延シミュレーション
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // 20%の確率でエラー
  if (Math.random() < 0.2) {
    throw new Error('AI分析に失敗しました');
  }
  
  // 顧客IDに基づいて一貫性のあるデータを生成
  const seed = customerId % 10;
  
  // 顧客タイプを決定
  const customerType = customerTypes[seed % customerTypes.length];
  
  // 行動パターンを3-4個選択
  const patternCount = 3 + (seed % 2);
  const patterns: string[] = [];
  for (let i = 0; i < patternCount; i++) {
    const index = (seed + i) % behavioralPatternsPool.length;
    patterns.push(behavioralPatternsPool[index]);
  }
  
  // おすすめサービスを3-5個選択
  const serviceCount = 3 + (seed % 3);
  const services: string[] = [];
  for (let i = 0; i < serviceCount; i++) {
    const index = (seed + i * 2) % recommendedServicesPool.length;
    services.push(recommendedServicesPool[index]);
  }
  
  // サービスノートを2個選択
  const notes: string[] = [];
  for (let i = 0; i < 2; i++) {
    const index = (seed + i * 3) % serviceNotesPool.length;
    notes.push(serviceNotesPool[index]);
  }
  
  // 特定の顧客タイプに応じた調整
  if (customerType === 'VIP') {
    patterns.unshift('月3回以上の高頻度来店');
    services.unshift('VIP限定特別コース');
  } else if (customerType === '新規') {
    patterns.unshift('初回来店から日が浅い');
    services.unshift('初回限定お試しコース');
  } else if (customerType === '休眠顧客') {
    patterns.unshift('3ヶ月以上来店なし');
    services.unshift('カムバックキャンペーン');
  }
  
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7日後
  
  return {
    customer_type: customerType,
    behavioral_patterns: patterns,
    recommended_services: services,
    service_notes: notes,
    generated_at: now.toISOString(),
    expires_at: expiresAt.toISOString(),
    is_cached: false
  };
};

/**
 * キャッシュされたプロファイルを取得（モック）
 * @param customerId 顧客ID
 * @returns キャッシュされたAI分析結果またはnull
 */
export const mockGetCachedProfile = async (customerId: number): Promise<AIProfileSummary | null> => {
  // ローカルストレージからキャッシュを確認
  const cacheKey = `ai_profile_${customerId}`;
  const cached = localStorage.getItem(cacheKey);
  
  if (cached) {
    const data = JSON.parse(cached);
    const expiresAt = new Date(data.expires_at);
    
    // 有効期限をチェック
    if (expiresAt > new Date()) {
      return { ...data, is_cached: true };
    } else {
      // 期限切れの場合は削除
      localStorage.removeItem(cacheKey);
    }
  }
  
  return null;
};

/**
 * プロファイルをキャッシュに保存（モック）
 * @param customerId 顧客ID
 * @param profile AI分析結果
 */
export const mockSaveProfileCache = async (customerId: number, profile: AIProfileSummary): Promise<void> => {
  const cacheKey = `ai_profile_${customerId}`;
  localStorage.setItem(cacheKey, JSON.stringify(profile));
};

/**
 * バッチ分析の実行（モック）
 * @param customerIds 顧客IDの配列
 * @returns 分析結果の配列
 */
export const mockBatchAnalysis = async (customerIds: number[]): Promise<Map<number, AIProfileSummary>> => {
  const results = new Map<number, AIProfileSummary>();
  
  // 並列処理のシミュレーション
  const promises = customerIds.map(async (id) => {
    try {
      const profile = await mockGenerateProfileSummary(id);
      results.set(id, profile);
    } catch (error) {
      logger.error(`Failed to analyze customer ${id}:`, error);
    }
  });
  
  await Promise.all(promises);
  return results;
};